import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { api, useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Field, inputClass } from '../onboarding/Choices';
import { PreferencesSection } from './PreferencesSection';
import {
  COUNTRIES,
  LANGUAGES,
  PROFESSIONAL_BACKGROUNDS,
  ORGANIZATION_TYPES,
} from '../../lib/investorProfileOptions';

/**
 * The platform profile - the same light set of facts captured at onboarding. Identity documents,
 * addresses and tax numbers are deliberately absent: those belong to the external compliance
 * provider, at the point of investing.
 */
const ProfileTab = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [investorType, setInvestorType] = useState('individual');
  const [hasInvestments, setHasInvestments] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get('/onboarding/profile/state')
      .then(({ data }) => {
        setProfile(data.profile || {});
        setInvestorType(data.investor_type || 'individual');
        setHasInvestments(Boolean(data.has_investments));
      })
      .catch(() => setProfile({}));
  }, []);

  if (!profile) return null;

  const isCompany = investorType === 'company';
  const set = (patch) => setProfile((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { email, ...fields } = profile;
      await api.put('/onboarding/profile/basic', { ...fields, investor_type: investorType });
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Could not save your profile');
    } finally {
      setSaving(false);
    }
  };

  const text = (name, label, { optional = false, list } = {}) => (
    <Field label={label} optional={optional} htmlFor={name}>
      <input
        id={name}
        list={list}
        className={inputClass}
        value={profile[name] || ''}
        onChange={(e) => set({ [name]: e.target.value })}
        data-testid={`profile-${name.replace(/_/g, '-')}`}
      />
    </Field>
  );

  const select = (name, label, options, { optional = false } = {}) => (
    <Field label={label} optional={optional} htmlFor={name}>
      <select
        id={name}
        className={inputClass}
        value={profile[name] || ''}
        onChange={(e) => set({ [name]: e.target.value })}
        data-testid={`profile-${name.replace(/_/g, '-')}`}
      >
        <option value="">Select…</option>
        {options.map((option) => (
          <option key={option.value || option} value={option.value || option}>
            {option.label || option}
          </option>
        ))}
      </select>
    </Field>
  );

  return (
    <div className="space-y-6" data-testid="profile-tab">
      <Card data-testid="platform-profile-card">
        <CardHeader>
          <CardTitle className="text-lg">
            {isCompany ? 'Organization Information' : 'Personal Information'}
          </CardTitle>
          <CardDescription>
            How we address you on Flippex. Verification of your legal identity happens separately,
            through our compliance provider, when you invest.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {isCompany ? (
            <>
              <div className="sm:col-span-2">{text('organization_name', 'Organization name')}</div>
              {select('organization_type', 'Organization type', ORGANIZATION_TYPES)}
              {text('country_of_incorporation', 'Country of incorporation', { list: 'profile-countries' })}
              {text('representative_first_name', 'Representative first name')}
              {text('representative_last_name', 'Representative last name')}
              {text('representative_role', 'Representative role', { optional: true })}
              {text('company_website', 'Company website', { optional: true })}
            </>
          ) : (
            <>
              {text('first_name', 'First name')}
              {text('last_name', 'Last name')}
              {text('country_of_residence', 'Country of residence', { list: 'profile-countries' })}
              {text('nationality', 'Nationality', { optional: true, list: 'profile-countries' })}
              {text('phone', 'Phone number', { optional: true })}
              {select('professional_background', 'Professional background', PROFESSIONAL_BACKGROUNDS, {
                optional: true,
              })}
            </>
          )}
          {select('preferred_language', 'Preferred language', LANGUAGES)}

          <Field label="Email" htmlFor="profile-email">
            <input
              id="profile-email"
              className={`${inputClass} opacity-60`}
              value={profile.email || user?.email || ''}
              readOnly
              data-testid="profile-email"
            />
            <p className="text-xs text-foreground-muted mt-1.5">
              Change your email in the Account tab — it needs a new verification code.
            </p>
          </Field>

          <datalist id="profile-countries">
            {COUNTRIES.map((country) => (
              <option key={country} value={country} />
            ))}
          </datalist>
        </CardContent>
      </Card>

      {hasInvestments && (
        <p className="text-xs text-foreground-muted" data-testid="verified-identity-note">
          You hold investments on Flippex. You can keep these platform details up to date here, but
          the legal investor details behind an existing investment are confirmed with our compliance
          provider and may need re-verification to change.
        </p>
      )}

      <div className="flex justify-end" data-testid="save-profile-section">
        <Button onClick={save} disabled={saving} data-testid="save-profile-button">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <PreferencesSection />
    </div>
  );
};

export default ProfileTab;
